/**
 * Actuarial rate table — backed by the real `GET /v2/actuarial-rates`
 * endpoint (orion-backend PR #261).
 *
 * The backend row shape (`ActuarialRateGet`: state + urbanicity +
 * `year_N`) is adapted here to the FE `RateRow` that `domain/pricing`
 * consumes (composed `code` + `yearN`), so the pricing domain stays
 * untouched.
 *
 * Note: the dev table was seeded from the legacy `rates.json`. It holds
 * 71 rows — every state's Urban + ROS variant — EXCEPT `DC`, which the
 * backend `USState` enum doesn't accept. A facility in DC therefore has
 * no rate (the lookup renders "—").
 */
import type { RateRow } from "../../domain/pricing";
import { getJsonReal } from "../client";

export interface RatesResponse {
    rates: RateRow[];
}

/** Backend actuarial-rate row — mirrors `ActuarialRateGet`. */
interface ActuarialRateWire {
    id: string;
    state: string;
    urbanicity: string;
    year_1: number | null;
    year_2: number | null;
    year_3: number | null;
    year_4: number | null;
    mature: number | null;
    created_at: string;
    updated_at: string;
}

/** Backend row → FE `RateRow`. The lookup `code` is the bare state
 *  code for Urban rows and the `"<state> - ROS"` variant for Rural. */
function toRateRow(r: ActuarialRateWire): RateRow {
    return {
        code: r.urbanicity === "Rural" ? `${r.state} - ROS` : r.state,
        state: r.state,
        year1: r.year_1 ?? 0,
        year2: r.year_2 ?? 0,
        year3: r.year_3 ?? 0,
        year4: r.year_4 ?? 0,
        mature: r.mature ?? 0,
        // Not modelled on the backend — informational only, the lookup
        // runs on the composed `code`.
        urban_counties: [],
    };
}

/** Backend caps `limit` at 200 (a higher value 422s), so we page through
 *  the table to fetch ALL rows — never truncated regardless of how large
 *  the table grows (today ~102 rows: state × Urban/ROS). */
const RATES_PAGE_SIZE = 200;

export async function getRates(): Promise<RatesResponse> {
    const all: ActuarialRateWire[] = [];
    for (let offset = 0; ; offset += RATES_PAGE_SIZE) {
        const page = await getJsonReal<ActuarialRateWire[]>(
            `/v2/actuarial-rates/?limit=${RATES_PAGE_SIZE}&offset=${offset}`,
        );
        all.push(...page);
        if (page.length < RATES_PAGE_SIZE) break;
    }
    return { rates: all.map(toRateRow) };
}
